import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Navbar } from "../components/Navbar";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, XCircle, Loader2, Trophy, RotateCcw } from "lucide-react";

export default function QuizView() {
  const { moduleId } = useParams();
  const nav = useNavigate();
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setLoading(true);
    setResult(null);
    api.get(`/quizzes/${moduleId}`).then((r) => {
      setQuiz(r.data);
      setAnswers(new Array(r.data.questions.length).fill(-1));
    }).catch((e) => {
      if (e.response?.status === 402) nav("/checkout");
      else nav("/dashboard");
    }).finally(() => setLoading(false));
  };
  useEffect(load, [moduleId]);

  if (loading || !quiz)
    return (
      <div className="min-h-screen"><Navbar />
        <div className="flex items-center justify-center h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      </div>
    );

  const select = (qi, oi) => {
    if (result) return;
    setAnswers((a) => a.map((v, i) => (i === qi ? oi : v)));
  };

  const submit = async () => {
    if (answers.includes(-1)) {
      toast.error("Please answer every question.");
      return;
    }
    setSubmitting(true);
    const res = await api.post(`/quizzes/${moduleId}/submit`, { module_id: moduleId, answers });
    setSubmitting(false);
    setResult(res.data);
    window.scrollTo({ top: 0, behavior: "smooth" });
    if (res.data.passed) toast.success(`Passed with ${res.data.percent}%!`);
    else toast.error(`Scored ${res.data.percent}%. 70% needed to pass.`);
  };

  return (
    <div className="min-h-screen pb-16">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 pt-6">
        <Link to={`/module/${moduleId}`} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-5" data-testid="quiz-back">
          <ArrowLeft className="h-4 w-4" /> Back to module
        </Link>

        <span className="text-xs font-mono uppercase tracking-[0.3em] text-accent">Mission Check</span>
        <h1 className="font-display text-3xl font-bold mb-1 mt-2">{quiz.title}</h1>
        <p className="text-sm text-muted-foreground font-mono mb-8">{quiz.questions.length} questions · 70% to pass</p>

        {result && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className={`p-6 rounded-sm border mb-8 flex items-center gap-5 ${result.passed ? "border-primary/50 bg-primary/5" : "border-destructive/50 bg-destructive/5"}`}
            data-testid="quiz-result">
            <div className={`h-14 w-14 rounded-sm flex items-center justify-center shrink-0 ${result.passed ? "bg-primary/15" : "bg-destructive/15"}`}>
              {result.passed ? <Trophy className="h-7 w-7 text-primary" /> : <RotateCcw className="h-7 w-7 text-destructive" />}
            </div>
            <div>
              <div className="font-display text-2xl font-bold">{result.passed ? "Cleared!" : "Not yet"}</div>
              <div className="font-mono text-sm text-muted-foreground">
                {result.correct}/{result.total} correct · <span className={result.passed ? "text-primary" : "text-destructive"}>{result.percent}%</span>
              </div>
            </div>
          </motion.div>
        )}

        <div className="space-y-5">
          {quiz.questions.map((q, qi) => (
            <motion.div key={qi} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: qi * 0.05 }}
              className="p-5 rounded-sm border border-border/70 bg-card" data-testid={`quiz-question-${qi}`}>
              <p className="font-medium mb-4"><span className="font-mono text-primary mr-2">{String(qi + 1).padStart(2, "0")}</span>{q.q}</p>
              <div className="space-y-2">
                {q.options.map((opt, oi) => {
                  const chosen = answers[qi] === oi;
                  let cls = "border-border/70 hover:border-border bg-secondary/30";
                  let icon = null;
                  if (result) {
                    const correctIdx = result.results[qi].correct_index;
                    if (oi === correctIdx) { cls = "border-primary/60 bg-primary/10"; icon = <CheckCircle2 className="h-4 w-4 text-primary" />; }
                    else if (chosen) { cls = "border-destructive/60 bg-destructive/10"; icon = <XCircle className="h-4 w-4 text-destructive" />; }
                    else cls = "border-border/50 opacity-60";
                  } else if (chosen) {
                    cls = "border-primary/60 bg-primary/10";
                  }
                  return (
                    <button key={oi} onClick={() => select(qi, oi)} disabled={!!result}
                      data-testid={`quiz-q${qi}-opt${oi}`}
                      className={`w-full text-left px-4 py-3 rounded-sm border text-sm transition-colors flex items-center justify-between gap-3 ${cls}`}>
                      <span>{opt}</span>{icon}
                    </button>
                  );
                })}
              </div>
              {result && result.results[qi]?.explanation && (
                <div className="mt-3 flex items-start gap-2 p-3 rounded-sm border border-primary/25 bg-primary/5"
                  data-testid={`quiz-q${qi}-explanation`}>
                  <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <p className="text-sm text-slate-300 leading-relaxed">{result.results[qi].explanation}</p>
                </div>
              )}
            </motion.div>
          ))}
        </div>

        <div className="mt-8 flex gap-3">
          {!result ? (
            <Button onClick={submit} disabled={submitting} data-testid="quiz-submit"
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm h-11 px-8 font-semibold">
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Submit answers
            </Button>
          ) : (
            <>
              <Button onClick={load} variant="outline" className="rounded-sm h-11" data-testid="quiz-retry">
                <RotateCcw className="mr-2 h-4 w-4" /> Retry
              </Button>
              <Link to={`/module/${moduleId}`}>
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm h-11" data-testid="quiz-done">
                  Back to module
                </Button>
              </Link>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
